/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import CompetitorAnalysisService from '../../services/CompetitorAnalysisService';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Rating,
  Tooltip,
  Divider,
  Paper,
  Stack,
  Slider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import WebsiteIcon from '@mui/icons-material/Language';
import PhotoIcon from '@mui/icons-material/Photo';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { keyframes } from '@mui/system';
import { GooglePlacesService } from './dataforseo-places.js';
import { DataExportService } from './data-export.js';
import { RankingService } from './ranking-service.js';
import HeatmapPreviewMap from '../Heatmap/HeatmapPreviewMap';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';
import TrendingUpIcon from './icons/TrendingUpIcon';
import TrendingDownIcon from './icons/TrendingDownIcon';
import { getSession } from '../../utils/authUtils';
import { getCompetitorGmbsLimit } from '../../services/SubscriptionService';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';

// Keyframe Animations
const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const rippleAnimation = keyframes`
  0% { 
    transform: scale(0.8); 
    opacity: 1; 
  }
  100% { 
    transform: scale(2.5); 
    opacity: 0; 
  }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

// Styled Components
const ModalContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px'
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '32px',
  width: '95vw',
  maxWidth: '1400px',
  maxHeight: '90vh',
  overflow: 'auto',
  outline: 'none',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.down('md')]: {
    padding: '24px',
    width: '90vw'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    width: '95vw',
    borderRadius: '8px'
  }
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  paddingBottom: '16px',
  borderBottom: '1px solid #e5e7eb',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '16px',
    paddingBottom: '12px'
  }
}));

const ModalTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  color: '#1f2937',
  [theme.breakpoints.down('md')]: {
    fontSize: '20px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px'
  }
}));

// Compare with Competitors Section Components
const CompetitorSection = styled(Box)(({ theme }) => ({
  marginBottom: '32px'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '28px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    marginBottom: '16px'
  }
}));

const KeywordTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  marginBottom: '24px',
  flexWrap: 'wrap'
}));

const KeywordTab = styled(Button)(({ active, theme }) => ({
  backgroundColor: active ? '#0B91D6' : '#f3f4f6',
  color: active ? 'white' : '#374151',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: active ? '#0980c2' : '#e5e7eb'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '6px 12px'
  }
}));

// Ranking Podium Component
const RankingPodium = styled(Box)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    marginBottom: '16px',
    borderRadius: '8px'
  }
}));

const PodiumContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  gap: '20px',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
    transform: 'scale(0.85)'
  }
}));

const PodiumStep = styled(Box)(({ position }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative'
}));

const CompetitorAvatar = styled(Box)(({ theme }) => ({
  width: '42px',
  height: '42px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: '0.6px solid #f6f0f0',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 500,
  color: '#121927',
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    fontSize: '10px'
  }
}));

const CompetitorName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '8px',
  maxWidth: '70px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    maxWidth: '60px'
  }
}));

const PodiumBase = styled(Box)(({ position, theme }) => ({
  width: '80px',
  height: position === 1 ? '80px' : position === 2 ? '60px' : '40px',
  backgroundColor: position === 1 ? 'rgba(255, 184, 0, 0.2)' : 'rgba(255, 255, 255, 0.1)',
  borderRadius: '8px 8px 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 600,
  color: '#ffffff',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    height: position === 1 ? '60px' : position === 2 ? '45px' : '30px',
    fontSize: '18px'
  }
}));

const CrownIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-12px',
  fontSize: '20px',
  color: '#FFB800'
}));

// Comparison Table Components
const ComparisonTable = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #e5e7eb',
  marginBottom: '24px',
  [theme.breakpoints.down('md')]: {
    overflowX: 'auto'
  }
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  padding: '16px',
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
    padding: '12px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '10px 8px',
    whiteSpace: 'nowrap'
  }
}));

const TableDataCell = styled(TableCell)(({ isMyBusiness, theme }) => ({
  backgroundColor: isMyBusiness ? '#dcfce7' : 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  padding: '16px',
  borderBottom: '1px solid #f3f4f6',
  '&:hover': {
    backgroundColor: isMyBusiness ? '#dcfce7' : '#f9fafb'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
    padding: '12px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '10px 8px'
  }
}));

const RankBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  fontWeight: 500
}));

const ScoreText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const BusinessNameCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
}));

const BusinessName = styled(Typography)(({ isMyBusiness, theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: isMyBusiness ? '#10b981' : '#121927',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: isMyBusiness ? '#059669' : '#0B91D6'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px'
  }
}));

const BusinessAddress = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 400,
  color: '#a0a0aa',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '200px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
    maxWidth: '150px'
  }
}));

const BusinessIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  marginTop: '2px'
}));

// Smart Radius Configuration Components
const ConfigContainer = styled(Stack)(({ theme }) => ({
  gap: '24px',
  direction: 'column',
  alignItems: 'center'
}));

const ConfigRow = styled(Stack)(({ theme }) => ({
  direction: 'row',
  gap: '24px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    width: '100%'
  }
}));

const ConfigColumn = styled(Stack)(({ theme }) => ({
  gap: '24px',
  flex: 1
}));

const SelectorContainer = styled(Stack)(({ theme }) => ({
  gap: '16px'
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 400,
  color: '#121927',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px'
  }
}));

const RadiusValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6'
}));

const UnitSelector = styled(Stack)(({ theme }) => ({
  direction: 'row',
  gap: '14px',
  flexWrap: 'wrap'
}));

const UnitButton = styled(Typography)(({ theme, selected }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: selected ? '#ffffff' : '#121927',
  backgroundColor: selected ? '#34A853' : '#ffffff',
  padding: '8px 16px',
  borderRadius: '4px',
  cursor: 'pointer',
  border: selected ? 'none' : '1px solid #E5E7EB',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: selected ? '#10B981' : '#F3F4F6'
  }
}));

const GridButton = styled(Button)(({ active, theme }) => ({
  backgroundColor: active ? '#0B91D6' : '#f3f4f6',
  color: active ? 'white' : '#374151',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '12px 16px',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: active ? '#0980c2' : '#e5e7eb'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '10px 14px'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '12px 24px',
  '&:hover': {
    backgroundColor: '#0980c2'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    padding: '10px 20px',
    width: '100%'
  }
}));

// Premium Professional Loading Components
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 40px',
  background: 'linear-gradient(145deg, #1a1f35 0%, #0f1419 100%)',
  borderRadius: '24px',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(11,145,214,0.15) 0%, transparent 70%)',
    animation: `${pulseAnimation} 4s ease-in-out infinite`
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'conic-gradient(from 0deg, transparent 0deg, rgba(11,145,214,0.08) 180deg, transparent 360deg)',
    animation: `${rotateAnimation} 8s linear infinite`
  },
  [theme.breakpoints.down('sm')]: {
    padding: '60px 20px'
  }
}));

const LoadingCircleContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '48px',
  zIndex: 2
}));

const AnimatedCircle = styled(Box)(({ delay }) => ({
  position: 'absolute',
  width: '240px',
  height: '240px',
  borderRadius: '50%',
  border: '2px solid transparent',
  borderImage: 'linear-gradient(135deg, rgba(11,145,214,0.4), rgba(147,51,234,0.4)) 1',
  animation: `${rippleAnimation} 3s infinite ${delay}s ease-out`,
  '@media (max-width: 600px)': {
    width: '180px',
    height: '180px'
  }
}));

const MainProgressCircle = styled(Box)(({ theme }) => ({
  width: '200px',
  height: '200px',
  borderRadius: '50%',
  background: 'linear-gradient(145deg, #0B91D6 0%, #9333ea 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  boxShadow: `
    0 0 0 8px rgba(11,145,214,0.1),
    0 0 0 16px rgba(11,145,214,0.05),
    0 20px 60px rgba(11, 145, 214, 0.5),
    inset 0 2px 4px rgba(255,255,255,0.2)
  `,
  position: 'relative',
  zIndex: 2,
  '@media (max-width: 600px)': {
    width: '160px',
    height: '160px'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-4px',
    left: '-4px',
    right: '-4px',
    bottom: '-4px',
    borderRadius: '50%',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), transparent)',
    zIndex: -1
  }
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  fontSize: '56px',
  fontWeight: 800,
  color: '#ffffff',
  textShadow: '0 4px 20px rgba(0,0,0,0.4)',
  letterSpacing: '-2px',
  '@media (max-width: 600px)': {
    fontSize: '42px'
  }
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.95)',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px',
  '@media (max-width: 600px)': {
    fontSize: '11px',
    letterSpacing: '1.5px'
  }
}));

const StatusText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '32px',
  padding: '16px 32px',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  animation: `${fadeInUp} 0.5s ease-out`,
  position: 'relative',
  zIndex: 2,
  '@media (max-width: 600px)': {
    fontSize: '16px',
    padding: '12px 24px',
    marginBottom: '24px'
  }
}));

const AnimatedIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '24px',
  marginTop: '32px',
  '@media (max-width: 600px)': {
    gap: '16px',
    marginTop: '24px'
  }
}));

const AnimatedIconBox = styled(Box)(({ delay, theme }) => ({
  width: '72px',
  height: '72px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(20px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  animation: `${floatAnimation} 3s ease-in-out infinite ${delay}s`,
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '2px',
    background: 'linear-gradient(145deg, rgba(11,145,214,0.5), rgba(147,51,234,0.5))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0.6
  },
  '@media (max-width: 600px)': {
    width: '56px',
    height: '56px',
    borderRadius: '12px'
  }
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '500px',
  height: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '10px',
  overflow: 'hidden',
  marginTop: '24px',
  position: 'relative',
  '@media (max-width: 600px)': {
    maxWidth: '300px',
    height: '6px'
  }
}));

const ProgressBarFill = styled(Box)(({ progress }) => ({
  height: '100%',
  background: 'linear-gradient(90deg, #34A853 0%, #0B91D6 50%, #667eea 100%)',
  borderRadius: '10px',
  width: `${progress}%`,
  transition: 'width 0.3s ease-out',
  boxShadow: '0 0 20px rgba(52, 168, 83, 0.5)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
    animation: `${shimmerAnimation} 2s infinite`
  }
}));

// Default keywords for analysis (fallback)
const DEFAULT_ANALYSIS_KEYWORDS = [
  'Restaurant',
  'Hospital',
  'Digital Marketing',
  'Dentist',
  'Lawyer',
  'Gym'
];

// Grid size options
const GRID_SIZES = ['3*3', '5*5', '7*7', '9*9'];

const STORAGE_PREFIX = 'visibeen_competitor_analysis_';

const CompetitorAnalysisModal = ({
  open,
  onClose,
  businessData,
  businessLocation,
  businessName,
  locationId,  // Add location_id for accurate DB lookup
  onBusinessProfileClick,
  analysisStorageKey,
  onAnalysisSaved,
  analysisLocked = false,
  daysUntilNextAnalysis = 0,
  currentPlan = 'basic'  // Plan for limiting visible competitors: basic=2, advance=3, pro=5
}) => {
  const navigate = useNavigate();
  // Smart Radius Configuration State
  const [formData, setFormData] = useState({
    radiusValue: 2,
    radiusUnit: 'Km',
    mapGrid: '3*3',
    center: null
  });

  const [selectedKeywords, setSelectedKeywords] = useState([]); // Array of strings
  const [customKeyword, setCustomKeyword] = useState('');
  const [isCustomKeywordSelected, setIsCustomKeywordSelected] = useState(false);
  const [dynamicKeywords, setDynamicKeywords] = useState(DEFAULT_ANALYSIS_KEYWORDS);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [customKeywords, setCustomKeywords] = useState([]); // Store up to 5 custom keywords
  const [keywordResults, setKeywordResults] = useState({}); // Store results for each keyword
  const [activeKeywordTab, setActiveKeywordTab] = useState(null); // Track which keyword's results to show
  const [showKeywordInput, setShowKeywordInput] = useState(false); // Show/hide keyword input field
  const [newKeywordInput, setNewKeywordInput] = useState(''); // Input value for new keyword

  // Toggle keyword selection (max 5)
  const toggleKeyword = (keyword) => {
    setSelectedKeywords(prev => {
      if (prev.includes(keyword)) {
        return prev.filter(k => k !== keyword);
      } else {
        if (prev.length >= 5) {
          setError('You can select up to 5 keywords only.');
          setTimeout(() => setError(''), 3000);
          return prev;
        }
        return [...prev, keyword];
      }
    });
  };

  const [competitorData, setCompetitorData] = useState(null);
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rankingService, setRankingService] = useState(null);
  const [dataExportService] = useState(new DataExportService());
  const [apiHealthStatus, setApiHealthStatus] = useState(null);
  const [lastAnalysisId, setLastAnalysisId] = useState(null);
  const [exportFormat, setExportFormat] = useState('csv');
  const [showGridView, setShowGridView] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 100, status: 'Initializing...' });
  const [isDataFromCache, setIsDataFromCache] = useState(false);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [backgroundAnalysisId, setBackgroundAnalysisId] = useState(null);
  const lockDays = daysUntilNextAnalysis > 0 ? daysUntilNextAnalysis : 1;
  const lockDaysLabel = lockDays === 1 ? '1 day' : `${lockDays} days`;
  const analysisLockMessage = analysisLocked
    ? `Reanalysis locked. Next run available in ${lockDaysLabel}.`
    : '';

  // Check if current user is admin
  const isAdmin = React.useMemo(() => {
    const session = getSession();
    const adminRoles = [9];
    return session && adminRoles.includes(session.role_id);
  }, []);

  // Get plan from context for reactive updates (fallback to prop)
  const { planCode: contextPlanCode, isLoading: planLoading } = useProfilePlan();

  // PRIORITY: Use prop (from BusinessProfile cached value) over context to avoid wrong backend data
  const effectivePlan = React.useMemo(() => {
    // Priority 1: If prop has a valid plan (not default 'basic'), use it
    // This comes from BusinessProfile's cached planCode which is more reliable
    if (currentPlan && currentPlan !== 'basic') {
      return currentPlan;
    }

    // Priority 2: If context has a valid plan, use it
    if (contextPlanCode && contextPlanCode !== 'none' && !planLoading) {
      return contextPlanCode;
    }

    // Priority 3: Fallback to basic or whatever is available
    return contextPlanCode || currentPlan || 'basic';
  }, [contextPlanCode, currentPlan, planLoading]);

  logger.log('[CompetitorAnalysisModal] 📊 Plan info:', {
    prop: currentPlan,
    context: contextPlanCode,
    effective: effectivePlan,
    loading: planLoading
  });

  const storageKey = React.useMemo(() => {
    if (analysisStorageKey) return analysisStorageKey;
    if (businessData?.name) {
      return `${STORAGE_PREFIX}${businessData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
    }
    if (businessName) {
      return `${STORAGE_PREFIX}${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
    }
    return null;
  }, [analysisStorageKey, businessData?.name, businessName]);

  // Persist analysis progress to localStorage
  const persistAnalysisProgress = useCallback((progressData) => {
    if (!storageKey) return;
    try {
      const progressKey = `${storageKey}_progress`;
      localStorage.setItem(progressKey, JSON.stringify({
        ...progressData,
        lastUpdated: new Date().toISOString()
      }));
      logger.log('[CompetitorAnalysisModal] 💾 Progress saved:', progressData.status);
    } catch (err) {
      logger.error('[CompetitorAnalysisModal] Failed to save progress:', err);
    }
  }, [storageKey]);

  // Clear analysis progress
  const clearAnalysisProgress = useCallback(() => {
    if (!storageKey) return;
    try {
      const progressKey = `${storageKey}_progress`;
      localStorage.removeItem(progressKey);
      logger.log('[CompetitorAnalysisModal] 🗑️ Progress cleared');
    } catch (err) {
      logger.error('[CompetitorAnalysisModal] Failed to clear progress:', err);
    }
  }, [storageKey]);

  const persistAnalysisData = useCallback((data) => {
    if (!storageKey) {
      logger.warn('[CompetitorAnalysisModal] Cannot persist data: no storage key available');
      return;
    }
    try {
      const dataWithTimestamp = {
        ...data,
        savedAt: new Date().toISOString(),
        status: 'completed'
      };

      // Save individual keyword data (legacy support)
      localStorage.setItem(storageKey, JSON.stringify(dataWithTimestamp));

      // Save to multi-keyword storage
      const multiKeywordStorageKey = `${storageKey}_multi_keywords`;
      const existingMultiKeywordData = localStorage.getItem(multiKeywordStorageKey);
      const multiKeywordData = existingMultiKeywordData ? JSON.parse(existingMultiKeywordData) : {};

      // Update with new keyword data
      multiKeywordData[data.keyword] = dataWithTimestamp;
      localStorage.setItem(multiKeywordStorageKey, JSON.stringify(multiKeywordData));

      logger.log('[CompetitorAnalysisModal] ✅ Analysis data saved to localStorage with key:', storageKey);
      logger.log('[CompetitorAnalysisModal] 📊 Total keywords analyzed:', Object.keys(multiKeywordData).length);

      setIsDataFromCache(false); // Fresh data, not from cache
      clearAnalysisProgress(); // Clear progress when complete
      if (onAnalysisSaved) {
        // Pass isNewAnalysis flag to indicate this is fresh data that should be saved to DB
        onAnalysisSaved({ ...dataWithTimestamp, isNewAnalysis: true });
      }
    } catch (err) {
      logger.error('[CompetitorAnalysisModal] ❌ Failed to persist analysis data:', err);
    }
  }, [storageKey, onAnalysisSaved, clearAnalysisProgress]);

  // Load saved data and check for in-progress analysis when modal opens
  useEffect(() => {
    if (!open || !storageKey) return;

    logger.log('[CompetitorAnalysisModal] 🔍 Checking for saved analysis data with key:', storageKey);

    const fetchFromDatabase = async () => {
      try {
        const currentBusinessName = businessData?.name || businessData?.title;
        // Get location_id from prop or businessData
        const currentLocationId = locationId || businessData?.locationId || businessData?.location_id;

        if (!currentBusinessName && !currentLocationId) {
          logger.log('[CompetitorAnalysisModal] No business name or location_id available for DB fetch');
          return false;
        }

        // Check if admin is viewing client - use client's user ID
        let userId;
        try {
          const adminAccessData = sessionStorage.getItem('adminAccessData');
          if (adminAccessData) {
            const accessInfo = JSON.parse(adminAccessData);
            userId = accessInfo.targetUserId;
            logger.log('[CompetitorAnalysisModal] Admin viewing mode - using client user ID:', userId);
          }
        } catch (error) {
          logger.warn('[CompetitorAnalysisModal] Error checking admin access data:', error);
        }

        // Fallback to session user ID if not admin viewing
        if (!userId) {
          const session = getSession();
          userId = session?.id || session?.user?.id;
        }

        if (!userId) {
          logger.warn('[CompetitorAnalysisModal] Missing user ID, cannot fetch from database');
          return false;
        }

        logger.log('[CompetitorAnalysisModal] 🔄 Fetching competitor data from database for user:', userId);
        console.log('[CompetitorAnalysisModal] 🔍 DEBUG - Full fetch params:', {
          userId,
          currentLocationId,
          currentBusinessName,
          locationIdProp: locationId,
          businessDataLocationId: businessData?.locationId,
          businessDataLocation_id: businessData?.location_id,
          isAdminView: !!sessionStorage.getItem('adminAccessData')
        });

        // Prioritize location_id for accurate user-specific lookup
        const queryParams = { limit: 25 };
        if (currentLocationId) {
          queryParams.location_id = currentLocationId;
          logger.log('[CompetitorAnalysisModal] Using location_id for lookup:', currentLocationId);
        }
        if (currentBusinessName) {
          queryParams.business_name = currentBusinessName;
        }
        console.log('[CompetitorAnalysisModal] 🔍 DEBUG - Query params being sent:', queryParams);
        const historyResponse = await CompetitorAnalysisService.getHistory(userId, queryParams);
        console.log('[CompetitorAnalysisModal] 🔍 DEBUG - Raw API response:', historyResponse);

        let analyses = [];
        if (Array.isArray(historyResponse?.data?.analyses)) {
          analyses = historyResponse.data.analyses;
        } else if (Array.isArray(historyResponse?.data)) {
          analyses = historyResponse.data;
        } else if (Array.isArray(historyResponse?.analyses)) {
          analyses = historyResponse.analyses;
        }
        console.log('[CompetitorAnalysisModal] 🔍 DEBUG - Extracted analyses count:', analyses.length);

        if (!Array.isArray(analyses) || analyses.length === 0) {
          logger.log('[CompetitorAnalysisModal] No database history found');
          return false;
        }

        logger.log('[CompetitorAnalysisModal] ✅ Found', analyses.length, 'analyses in database');

        // Build keyword map from database results
        const keywordMap = {};
        analyses.forEach((analysis) => {
          if (!analysis?.keyword) return;

          const timestampIso = analysis.analysis_date
            ? new Date(analysis.analysis_date).toISOString()
            : new Date().toISOString();

          const rawCompetitors = analysis.competitors_data ?? analysis.competitorsData ?? [];
          let competitorsList = [];
          let gridData = null;

          if (Array.isArray(rawCompetitors)) {
            // Old format: competitors_data is directly an array
            competitorsList = rawCompetitors;
          } else if (typeof rawCompetitors === 'string') {
            // String format: parse JSON
            try {
              const parsed = JSON.parse(rawCompetitors);
              competitorsList = Array.isArray(parsed) ? parsed : (parsed.competitors || []);
              gridData = parsed.gridData || null;
            } catch (e) {
              logger.warn('[CompetitorAnalysisModal] Failed to parse competitors_data:', e);
            }
          } else if (typeof rawCompetitors === 'object' && rawCompetitors !== null) {
            // New format: competitors_data is an object with competitors array inside
            competitorsList = rawCompetitors.competitors || [];
            gridData = rawCompetitors.gridData || null;
          }

          const normalizedEntry = {
            keyword: analysis.keyword,
            competitors: competitorsList,
            gridData: gridData,
            myBusinessRank: analysis.my_business_rank,
            myBusinessScore: analysis.my_business_score,
            myBusinessName: analysis.business_name || currentBusinessName,
            totalFound: analysis.total_competitors || competitorsList.length || 0,
            timestamp: timestampIso,
            savedAt: timestampIso,
            status: 'completed',
            source: 'database'
          };

          const existing = keywordMap[analysis.keyword];
          // Only compare if we have a valid timestamp (not 1970)
          const newDate = new Date(timestampIso);
          const existingDate = existing?.timestamp ? new Date(existing.timestamp) : null;
          const isNewDateValid = !isNaN(newDate.getTime()) && newDate.getFullYear() >= 2000;
          const isExistingDateValid = existingDate && !isNaN(existingDate.getTime()) && existingDate.getFullYear() >= 2000;

          if (!existing || (isNewDateValid && (!isExistingDateValid || newDate > existingDate))) {
            keywordMap[analysis.keyword] = normalizedEntry;
          }
        });

        const keywordKeys = Object.keys(keywordMap);
        if (keywordKeys.length === 0) {
          return false;
        }

        // Filter out keywords with invalid timestamps before finding most recent
        const validKeywordKeys = keywordKeys.filter(kw => {
          const ts = keywordMap[kw]?.timestamp;
          if (!ts) return false;
          const date = new Date(ts);
          return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
        });

        if (validKeywordKeys.length === 0) {
          return false;
        }

        // Find most recent keyword
        const latestKeyword = validKeywordKeys.reduce((latest, current) => {
          if (!latest) return current;
          const latestTime = new Date(keywordMap[latest].timestamp);
          const currentTime = new Date(keywordMap[current].timestamp);
          return currentTime > latestTime ? current : latest;
        }, null);

        logger.log('[CompetitorAnalysisModal] ✅ Setting keyword results from database');
        setKeywordResults(keywordMap);
        setActiveKeywordTab(latestKeyword);
        setCompetitorData(keywordMap[latestKeyword]);
        setIsDataFromCache(true);

        // Notify parent component about the loaded data (from cache, don't save to DB again)
        if (onAnalysisSaved && keywordMap[latestKeyword]) {
          logger.log('[CompetitorAnalysisModal] ✅ Calling onAnalysisSaved with score:', keywordMap[latestKeyword].myBusinessScore);
          onAnalysisSaved({ ...keywordMap[latestKeyword], isNewAnalysis: false });
        }

        // Cache to localStorage
        const multiKeywordStorageKey = `${storageKey}_multi_keywords`;
        try {
          localStorage.setItem(multiKeywordStorageKey, JSON.stringify(keywordMap));
        } catch (e) {
          logger.warn('[CompetitorAnalysisModal] Failed to cache to localStorage:', e);
        }

        return true;
      } catch (error) {
        logger.error('[CompetitorAnalysisModal] Error fetching from database:', error);
        return false;
      }
    };

    const loadData = async () => {
      try {
        // Check for multiple keyword results in localStorage first
        const multiKeywordStorageKey = `${storageKey}_multi_keywords`;
        const savedMultiKeywordData = localStorage.getItem(multiKeywordStorageKey);

        if (savedMultiKeywordData) {
          const parsedMultiKeyword = JSON.parse(savedMultiKeywordData);
          logger.log('[CompetitorAnalysisModal] ✅ Loaded multi-keyword results:', Object.keys(parsedMultiKeyword));
          setKeywordResults(parsedMultiKeyword);

          // Set the most recent analysis as active
          const keywords = Object.keys(parsedMultiKeyword);
          if (keywords.length > 0) {
            // Filter out keywords with invalid timestamps before comparing
            const validKeywords = keywords.filter(kw => {
              const ts = parsedMultiKeyword[kw]?.timestamp;
              if (!ts) return false;
              const date = new Date(ts);
              return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
            });

            if (validKeywords.length > 0) {
              // Find the most recent keyword analysis
              const mostRecentKeyword = validKeywords.reduce((latest, current) => {
                const latestTime = new Date(parsedMultiKeyword[latest].timestamp);
                const currentTime = new Date(parsedMultiKeyword[current].timestamp);
                return currentTime > latestTime ? current : latest;
              });

              setActiveKeywordTab(mostRecentKeyword);
              setCompetitorData(parsedMultiKeyword[mostRecentKeyword]);
              setIsDataFromCache(true);

              // Notify parent component about the loaded data (from cache, don't save to DB again)
              if (onAnalysisSaved && parsedMultiKeyword[mostRecentKeyword]) {
                logger.log('[CompetitorAnalysisModal] ✅ Calling onAnalysisSaved (from localStorage) with score:', parsedMultiKeyword[mostRecentKeyword].myBusinessScore);
                onAnalysisSaved({ ...parsedMultiKeyword[mostRecentKeyword], isNewAnalysis: false });
              }
            }
          }
        } else {
          // No localStorage data, check legacy single keyword data first
          const savedData = localStorage.getItem(storageKey);
          if (savedData) {
            const parsed = JSON.parse(savedData);
            if (parsed.status === 'completed') {
              logger.log('[CompetitorAnalysisModal] ✅ Loaded completed analysis data from localStorage:', {
                keyword: parsed.keyword,
                totalCompetitors: parsed.totalFound,
                savedAt: parsed.savedAt,
                timestamp: parsed.timestamp
              });

              setCompetitorData(parsed);
              setKeywordResults({ [parsed.keyword]: parsed });
              setActiveKeywordTab(parsed.keyword);
              setIsDataFromCache(true);
              if (onAnalysisSaved) {
                onAnalysisSaved({ ...parsed, isNewAnalysis: false });
              }
            }
          } else {
            // No localStorage data at all, fetch from database
            logger.log('[CompetitorAnalysisModal] No localStorage data, fetching from database...');
            const fetchSuccess = await fetchFromDatabase();
            if (!fetchSuccess) {
              logger.log('[CompetitorAnalysisModal] ℹ️ No saved analysis data found in localStorage or database');
              setIsDataFromCache(false);
            }
          }
        }

        // Check for in-progress analysis
        const progressKey = `${storageKey}_progress`;
        const progressData = localStorage.getItem(progressKey);
        if (progressData) {
          const parsed = JSON.parse(progressData);
          const lastUpdated = new Date(parsed.lastUpdated);
          const now = new Date();
          const minutesSinceUpdate = (now - lastUpdated) / 1000 / 60;

          // If analysis was updated in last 5 minutes, show as in progress
          if (minutesSinceUpdate < 5) {
            logger.log('[CompetitorAnalysisModal] 🔄 Found in-progress analysis:', parsed.status);
            setProgress(parsed);
            setAnalysisInProgress(true);
            setBackgroundAnalysisId(parsed.analysisId);
          } else {
            // Stale progress data, clear it
            logger.log('[CompetitorAnalysisModal] 🗑️ Clearing stale progress data');
            clearAnalysisProgress();
          }
        }
      } catch (err) {
        logger.error('[CompetitorAnalysisModal] ❌ Failed to load saved analysis data:', err);
        setIsDataFromCache(false);
      }
    };

    loadData();
  }, [open, storageKey, clearAnalysisProgress]); // FIXED: Removed onAnalysisSaved from dependencies to prevent infinite loop

  // Clear cache handler
  const handleClearCache = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
      const multiKeywordStorageKey = `${storageKey}_multi_keywords`;
      localStorage.removeItem(multiKeywordStorageKey);
      setCompetitorData(null);
      setKeywordResults({});
      setActiveKeywordTab(null);
      setIsDataFromCache(false);
      logger.log('[CompetitorAnalysisModal] 🗑️ Cleared all cached analysis data');
    } catch (err) {
      logger.error('[CompetitorAnalysisModal] Failed to clear cache:', err);
    }
  }, [storageKey]);

  // Fetch dynamic keywords when modal opens and businessData is available
  useEffect(() => {
    const fetchDynamicKeywords = async () => {
      if (!open || !businessData) {
        setDynamicKeywords(DEFAULT_ANALYSIS_KEYWORDS);
        return;
      }

      try {
        setKeywordsLoading(true);
        logger.log('[CompetitorAnalysisModal] Fetching keywords for business:', businessData.name);

        // Extract business category and location from GMB data
        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(businessData);
        const location = GoogleAdsKeywordService.extractLocation(businessData);

        logger.log('[CompetitorAnalysisModal] Business category:', businessCategory, 'Location:', location);

        // Get enhanced keywords based on business category
        const keywords = GoogleAdsKeywordService.getEnhancedMockKeywords(businessCategory, location);

        // Extract just the search terms and take first 6 for selection
        const keywordTerms = keywords.slice(0, 6).map(k => k.searchTerm);

        logger.log('[CompetitorAnalysisModal] Generated keywords:', keywordTerms);
        setDynamicKeywords(keywordTerms);

        // Initialize with first keyword if selection is empty
        if (selectedKeywords.length === 0) {
          setSelectedKeywords([keywordTerms[0] || DEFAULT_ANALYSIS_KEYWORDS[0]]);
        }

      } catch (error) {
        logger.error('[CompetitorAnalysisModal] Error fetching dynamic keywords:', error);
        setDynamicKeywords(DEFAULT_ANALYSIS_KEYWORDS);
      } finally {
        setKeywordsLoading(false);
      }
    };

    fetchDynamicKeywords();
  }, [open, businessData]);

  // Get business coordinates from various sources
  const getBusinessCoordinates = useCallback(() => {
    let coordinates;
    if (businessLocation?.lat && businessLocation?.lng) {
      coordinates = { lat: businessLocation.lat, lng: businessLocation.lng };
    } else if (businessData?.latlng) {
      coordinates = {
        lat: businessData.latlng.latitude,
        lng: businessData.latlng.longitude
      };
    } else {
      // Default coordinates
      coordinates = { lat: 30.7333, lng: 76.7794 }; // Chandigarh
    }
    return coordinates;
  }, [businessLocation, businessData]);

  // Initialize services when modal opens
  useEffect(() => {
    const initializeService = async () => {
      const apiKey = process.env.REACT_APP_PLACES_API_KEY ||
        process.env.REACT_APP_GOOGLE_API_KEY ||
        process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

      logger.log('🔑 API Key available:', !!apiKey);
      logger.log('🔑 Environment check:', {
        REACT_APP_GOOGLE_API_KEY: !!process.env.REACT_APP_GOOGLE_API_KEY,
        REACT_APP_GMB_API_KEY: !!process.env.REACT_APP_GMB_API_KEY,
        REACT_APP_GOOGLE_PLACES_API_KEY: !!process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
        allEnvKeys: Object.keys(process.env).filter(k => k.includes('API')).length
      });

      if (apiKey) {
        // Initialize ranking service (Smart Radius functionality)
        // Enable live API to use production Visibeen backend endpoint
        const rankingServiceInstance = new RankingService(apiKey, true, true);
        setRankingService(rankingServiceInstance);

        // Set initial API health status
        setApiHealthStatus({
          googlePlaces: { isWorking: true, lastChecked: new Date(), responseTime: 150 },
          overall: 'healthy'
        });
      } else {
        logger.warn('⚠️ No Google API key found. Using demo mode.');

        // Mock services for demo mode
        const mockRankingService = {
          performRankingAnalysis: async (config, onProgress) => {
            // Simulate progress
            for (let i = 0; i <= 100; i += 10) {
              if (onProgress) onProgress(i, 100,);
              await new Promise(r => setTimeout(r, 100));
            }

            return {
              businessName: config.businessName,
              keyword: config.keyword,
              centerLocation: config.centerLocation,
              gridSize: config.gridSize,
              searchRadius: config.searchRadius,
              averageRank: 3.5,
              bestRank: 1,
              worstRank: 7,
              notRankedCount: 2,
              results: [
                {
                  gridPoint: { gridX: 0, gridY: 0, lat: 30.7333, lng: 76.7794, id: 'A1' },
                  businesses: [
                    { name: 'Demo Business 1', rating: 4.5, userRatingCount: 120, address: 'Demo Address 1', placeId: 'demo1', photoCount: 15, businessStatus: 'OPERATIONAL', websiteUri: 'https://example1.com' },
                    { name: 'Demo Business 2', rating: 4.2, userRatingCount: 89, address: 'Demo Address 2', placeId: 'demo2', photoCount: 8, businessStatus: 'OPERATIONAL' },
                    { name: businessName || 'Your Business', rating: 4.0, userRatingCount: 45, address: 'Your Address', placeId: 'your-business', photoCount: 10, businessStatus: 'OPERATIONAL' }
                  ],
                  targetBusinessRank: 3
                }
              ],
              timestamp: new Date()
            };
          }
        };
        setRankingService(mockRankingService);


        setApiHealthStatus({
          googlePlaces: { isWorking: true, lastChecked: new Date(), responseTime: 150 },
          overall: 'healthy'
        });
        setError('Demo mode: Using sample data. Add REACT_APP_GOOGLE_API_KEY for real data.');
      }

      // Set business center coordinates
      const coordinates = getBusinessCoordinates();
      logger.log('🔍 [DEBUG] Setting center coordinates:', coordinates);
      setFormData(prev => ({ ...prev, center: coordinates }));
    };

    if (open && !rankingService) {
      initializeService();
    }
  }, [open, rankingService, getBusinessCoordinates]);

  // Handle form data changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle unit change
  const handleUnitChange = (unit) => {
    setFormData(prev => ({ ...prev, radiusUnit: unit }));
  };

  // Calculate grid size number
  const gridSizeNumber = React.useMemo(() => {
    if (!formData?.mapGrid) return 5;
    const match = /([0-9]+)\*([0-9]+)/.exec(formData.mapGrid);
    return match ? Number(match[1]) : 5;
  }, [formData?.mapGrid]);

  // Convert radius to km if needed
  const radiusInKm = React.useMemo(() => {
    if (formData.radiusUnit === 'Miles') {
      return formData.radiusValue * 1.60934; // Convert miles to km
    }
    return formData.radiusValue;
  }, [formData.radiusValue, formData.radiusUnit]);

  // Smart Radius Analysis function
  const handleSmartRadiusAnalysis = useCallback(async () => {
    logger.log('🔍 [DEBUG] Analysis validation:', {
      rankingService: !!rankingService,
      formDataCenter: formData.center,
      selectedKeywords: selectedKeywords
    });

    if (analysisLocked) {
      logger.warn('[CompetitorAnalysisModal] Analysis blocked - 7-day restriction active', { daysUntilNextAnalysis });
      setError(analysisLockMessage || 'Competitor analysis is temporarily locked. Please check back later.');
      return;
    }

    if (!rankingService || !formData.center || selectedKeywords.length === 0) {
      logger.warn('🔍 [DEBUG] Analysis validation failed - missing required data');
      setError('Please select at least one keyword.');
      return;
    }

    // Validate center coordinates
    if (!formData.center.lat || !formData.center.lng ||
      typeof formData.center.lat !== 'number' ||
      typeof formData.center.lng !== 'number' ||
      isNaN(formData.center.lat) || isNaN(formData.center.lng)) {
      logger.error('🔍 [DEBUG] Invalid center coordinates:', formData.center);
      setError('Invalid location coordinates. Please refresh and try again.');
      return;
    }

    setAnalyzing(true);
    setAnalysisInProgress(true);
    setError('');

    try {
      logger.log('🎯 Starting OPTIMIZED Multi-Keyword Smart Radius Analysis for:', selectedKeywords);

      // Store results for all keywords being analyzed in this session
      const newKeywordResults = { ...keywordResults };
      let lastAnalyzedKeywordData = null;

      for (let k = 0; k < selectedKeywords.length; k++) {
        const analysisKeyword = selectedKeywords[k];
        const keywordProgressOffset = (k / selectedKeywords.length) * 100;
        const keywordProgressWeight = 100 / selectedKeywords.length;

        logger.log(`🚀 Analyzing keyword ${k + 1}/${selectedKeywords.length}: ${analysisKeyword}`);

        // Generate unique analysis ID for tracking
        const analysisId = `analysis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setBackgroundAnalysisId(analysisId);

        const initialProgress = {
          current: keywordProgressOffset,
          total: 100,
          status: `Initializing analysis for "${analysisKeyword}" (${k + 1}/${selectedKeywords.length})...`,
          analysisId,
          keyword: analysisKeyword
        };
        setProgress(initialProgress);
        persistAnalysisProgress(initialProgress);

        try {
          logger.log('🎯 Starting OPTIMIZED Smart Radius Analysis');
          logger.log('⚡ Speed Optimizations: Reduced grid sizes, fewer phases, parallel processing');

          // OPTIMIZED: Start with smaller grids and only 2 phases instead of 3
          const analysisPhases = [
            { radius: 2, grid: 5, progressStart: 10, progressWeight: 45 },  // 5x5 instead of 7x7 (60% faster)
            { radius: 3, grid: 7, progressStart: 55, progressWeight: 40 },  // 7x7 instead of 9x9 (30% faster)
          ];

          const countUniqueCompetitors = (rankingData) => {
            const competitorsMap = new Map();
            rankingData.results.forEach((result) => {
              (result.businesses || []).forEach((business) => {
                const key = business.placeId || `${business.name}-${business.address}`;
                if (!competitorsMap.has(key)) {
                  competitorsMap.set(key, business);
                }
              });
            });
            return competitorsMap.size;
          };

          let finalResult = null;
          const maxCompetitors = 50;

          for (const [index, phase] of analysisPhases.entries()) {
            const phaseProgress = {
              current: phase.progressStart,
              total: 100,
              status: `Phase ${index + 1}/${analysisPhases.length}: Analyzing ${phase.radius}km radius...`,
              analysisId,
              keyword: analysisKeyword,
              phase: index + 1,
              totalPhases: analysisPhases.length
            };
            setProgress(phaseProgress);
            persistAnalysisProgress(phaseProgress);

            const config = {
              businessName: businessName || 'Your Business',
              businessAddress: businessData?.storefrontAddress?.addressLines?.join(', ') || businessData?.address || '',
              keyword: analysisKeyword,
              centerLocation: formData.center,
              searchRadius: phase.radius,
              gridSize: phase.grid,
              googleApiKey: process.env.REACT_APP_PLACES_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY
            };

            const rankingData = await rankingService.performRankingAnalysis(
              config,
              (current, total, status) => {
                const phaseProgress = phase.progressStart + (current / total) * phase.progressWeight;
                // Scale phase progress to overall multi-keyword progress
                const overallProgress = keywordProgressOffset + (phaseProgress / 100) * keywordProgressWeight;

                const progressData = {
                  current: overallProgress,
                  total: 100,
                  status: status ? `[${k + 1}/${selectedKeywords.length}] ${status}` : `Analyzing keyword ${k + 1} of ${selectedKeywords.length}...`,
                  analysisId,
                  keyword: analysisKeyword,
                  phase: index + 1,
                  totalPhases: analysisPhases.length
                };
                setProgress(progressData);
                // Save progress every 10% of current keyword to reduce I/O overhead
                if (Math.floor(current / 10) !== Math.floor((current - 1) / 10)) {
                  persistAnalysisProgress(progressData);
                }
              }
            );

            finalResult = rankingData;
            const totalCompetitorsFound = countUniqueCompetitors(rankingData);
            const myBusinessRank = rankingData.averageRank;

            logger.log(`🎯 Phase ${index + 1} Results: ${totalCompetitorsFound} competitors, my rank: ${myBusinessRank}`);

            if (totalCompetitorsFound >= maxCompetitors) {
              setProgress({ current: 100, total: 100, status: 'Analysis complete!' });
              logger.log(`🛑 Stopping: Found ${maxCompetitors}+ competitors at ${phase.radius}km radius`);
              break; // Exit loop
            }

            // Stop if rank is poor (>8), but not on the final phase
            if (index < analysisPhases.length - 1) {
              if (myBusinessRank && myBusinessRank > 8) {
                setProgress({ current: 100, total: 100, status: 'Analysis complete!' });
                logger.log(`🛑 Stopping: Business rank ${myBusinessRank.toFixed(1)} is above 8 at ${phase.radius}km radius`);
                break; // Exit loop
              }
            } else {
              // Final phase is complete
              setProgress({ current: 100, total: 100, status: 'Analysis complete!' });
              logger.log('🏁 Analysis complete: Maximum radius and grid size reached');
            }
          }

          // Use the final result
          let rankingData = finalResult;

          logger.log('🎯 Smart Radius Results:', {
            averageRank: rankingData.averageRank,
            bestRank: rankingData.bestRank,
            worstRank: rankingData.worstRank,
            notRankedCount: rankingData.notRankedCount,
            gridSize: rankingData.gridSize,
            searchRadius: rankingData.searchRadius,
            totalGridPoints: rankingData.results.length
          });

          // Transform ranking data to competitor format
          const competitorsMap = new Map();

          // Debug: Log the ranking data structure
          logger.log('🔍 [DEBUG] Ranking data results:', rankingData.results.length);
          logger.log('🔍 [DEBUG] First result businesses:', rankingData.results[0]?.businesses?.slice(0, 2));

          // Aggregate competitors from all grid points
          rankingData.results.forEach((result) => {
            (result.businesses || []).forEach((business, index) => {
              logger.log(`🔍 [DEBUG] Processing business: ${business.name}`, {
                address: business.address,
                phone: business.phone || business.nationalPhoneNumber || business.internationalPhoneNumber,
                website: business.website || business.websiteUri,
                categories: business.categories || business.types,
                photoCount: business.photoCount,
                primaryType: business.primaryType,
                hasQA: business.hasQA,
                serviceArea: business.serviceArea,
                appointmentLinks: business.appointmentLinks,
                socialLinks: business.socialLinks,
                reviewResponseRate: business.reviewResponseRate,
                newReviewsLast30Days: business.newReviewsLast30Days
              });

              const key = business.placeId || `${business.name}-${business.address}`;
              const currentRank = index + 1;

              if (!competitorsMap.has(key)) {
                competitorsMap.set(key, {
                  ...business,
                  // Ensure all required fields are properly mapped
                  address: business.address || business.formattedAddress || 'Address not available',
                  phone: business.phone || business.nationalPhoneNumber || business.internationalPhoneNumber,
                  website: business.website || business.websiteUri,
                  categories: business.categories || business.types || [],
                  userRatingCount: business.userRatingCount || business.reviewCount || 0,
                  reviewCount: business.reviewCount || business.userRatingCount || 0,
                  photoCount: business.photoCount || 0,
                  openingHours: business.openingHours || [],
                  types: business.types || business.categories || [],
                  socialLinks: business.socialLinks || [],
                  appointmentLinks: business.appointmentLinks || [],
                  serviceArea: business.serviceArea,
                  hasQA: business.hasQA || false,
                  reviewResponseRate: business.reviewResponseRate,
                  newReviewsLast30Days: business.newReviewsLast30Days,
                  primaryType: business.primaryType || business.types?.[0],
                  // Grid analysis fields
                  gridAppearances: 1,
                  averageRank: currentRank,
                  bestRank: currentRank,
                  worstRank: currentRank,
                  totalRanks: [currentRank],
                  gridPenalties: [calculateGridRankPenalty(currentRank)], // Store penalty for each grid appearance
                  gmbScore: 0, // Will be calculated later with all competitors
                  isMyBusiness: business.name.toLowerCase().includes(businessName?.toLowerCase() || '')
                });
              } else {
                const existing = competitorsMap.get(key);
                existing.gridAppearances += 1;
                existing.totalRanks.push(currentRank);
                existing.gridPenalties.push(calculateGridRankPenalty(currentRank)); // Add penalty for this grid appearance
                existing.averageRank = existing.totalRanks.reduce((a, b) => a + b, 0) / existing.totalRanks.length;
                existing.bestRank = Math.min(existing.bestRank, currentRank);
                existing.worstRank = Math.max(existing.worstRank, currentRank);
              }
            });
          });

          // Convert to array and calculate GMB scores with competitor context
          const allCompetitors = Array.from(competitorsMap.values());

          // Extract city name from business address for scoring context
          const cityName = businessData?.storefrontAddress?.locality ||
            businessData?.address?.split(',')[1]?.trim() ||
            formData.center?.address?.split(',')[1]?.trim();

          // Calculate GMB scores for all competitors with comprehensive grid penalties
          const totalGridPoints = rankingData.results.length;

          // PERFORMANCE OPTIMIZATION: Pre-calculate grid-based penalty values once
          const getPenaltyValues = (gridSize) => {
            if (gridSize === 49) return { yellow: 0.25, red: 0.75, gray: 1.0 }; // 7x7
            if (gridSize === 81) return { yellow: 0.15, red: 0.45, gray: 0.60 }; // 9x9
            if (gridSize === 144) return { yellow: 0.085, red: 0.25, gray: 0.34 }; // 12x12
            return { yellow: 0.25, red: 0.75, gray: 1.0 }; // Default to 7x7
          };
          const penalties = getPenaltyValues(totalGridPoints);

          allCompetitors.forEach(competitor => {
            const baseGMBScore = calculateGMBScore(competitor, allCompetitors, cityName, analysisKeyword);

            // Calculate ranking color counts for penalty calculation (already available from competitor.totalRanks)
            const greenCount = competitor.totalRanks.filter(rank => rank >= 1 && rank <= 3).length;
            const yellowCount = competitor.totalRanks.filter(rank => rank >= 4 && rank <= 10).length;
            const redCount = competitor.totalRanks.filter(rank => rank >= 11 && rank <= 20).length;
            const grayCount = totalGridPoints - competitor.gridAppearances;

            // New penalty formula: (Yellow×penalty + Red×penalty + Gray×penalty) / 100
            const penaltyPercentage = (yellowCount * penalties.yellow + redCount * penalties.red + grayCount * penalties.gray) / 100;

            competitor.baseGMBScore = baseGMBScore;
            competitor.comprehensivePenalty = penaltyPercentage;
            competitor.gridCoverage = (competitor.gridAppearances / totalGridPoints) * 100;
            competitor.greenCount = greenCount;
            competitor.yellowCount = yellowCount;
            competitor.redCount = redCount;
            competitor.grayCount = grayCount;
            competitor.gmbScore = Math.max(0, baseGMBScore * (1 - penaltyPercentage));

            // PERFORMANCE: Reduced logging - only log summary for top 5 competitors
            if (allCompetitors.indexOf(competitor) < 5) {
              logger.log(`🏆 ${competitor.name}: Score ${competitor.gmbScore.toFixed(1)}/1000 (Base: ${baseGMBScore.toFixed(1)}, Penalty: ${(penaltyPercentage * 100).toFixed(1)}%)`);
            }
          });

          // PERFORMANCE OPTIMIZATION: Create a Map for O(1) lookup instead of O(n) find()
          const competitorScoreMap = new Map();
          allCompetitors.forEach(c => {
            const key = c.placeId || `${c.name}-${c.address}`;
            competitorScoreMap.set(key, c);
          });

          // Update ranking data businesses with GMB scores
          rankingData.results.forEach(result => {
            result.businesses.forEach(business => {
              // O(1) lookup instead of O(n) find()
              const businessKey = business.placeId || `${business.name}-${business.address}`;
              const competitor = competitorScoreMap.get(businessKey);

              if (competitor) {
                business.gmbScore = competitor.gmbScore;
                business.baseGMBScore = competitor.baseGMBScore;
                business.comprehensivePenalty = competitor.comprehensivePenalty;
                // Update enriched data including full photo count
                business.photoCount = competitor.photoCount || business.photoCount;
                business.phone = competitor.phone || business.phone;
                business.website = competitor.website || business.website;
                business.openingHours = competitor.openingHours || business.openingHours;
              }
            });
          });

          // Sort by penalized GMB score (primary) then average rank (secondary)
          const smartCompetitors = allCompetitors
            .sort((a, b) => {
              // Use gmbScore directly (already penalized at line 1310)
              const aScore = a.gmbScore || 0;
              const bScore = b.gmbScore || 0;

              // Primary sort: Penalized GMB Score (higher is better)
              if (bScore !== aScore) {
                return bScore - aScore;
              }
              // Secondary sort: Average rank (lower is better)
              return a.averageRank - b.averageRank;
            })
            .map((competitor, index) => ({
              ...competitor,
              rank: index + 1,
              rankingScore: calculateRankingScore(competitor, index + 1)
            }));

          logger.log('🎯 Final Smart Competitors:', smartCompetitors.map(c => ({
            name: c.name,
            averageRank: c.averageRank?.toFixed(1),
            bestRank: c.bestRank,
            gridAppearances: c.gridAppearances,
            gmbScore: c.gmbScore,
            baseGMBScore: c.baseGMBScore,
            comprehensivePenalty: c.comprehensivePenalty,
            gridCoverage: c.gridCoverage,
            greenCount: c.greenCount,
            yellowCount: c.yellowCount,
            redCount: c.redCount,
            grayCount: c.grayCount,
            isMyBusiness: c.isMyBusiness
          })));

          const myBusiness = smartCompetitors.find(c => c.isMyBusiness);

          // Calculate grid point breakdown from rankingData for Performance page
          // This ensures we always have grid data even if myBusiness is not found
          const greenGridPoints = rankingData.results?.filter(r =>
            r.targetBusinessRank !== null && r.targetBusinessRank >= 1 && r.targetBusinessRank <= 3
          ).length || 0;

          const yellowGridPoints = rankingData.results?.filter(r =>
            r.targetBusinessRank !== null && r.targetBusinessRank >= 4 && r.targetBusinessRank <= 7
          ).length || 0;

          const redGridPoints = rankingData.results?.filter(r =>
            r.targetBusinessRank !== null && r.targetBusinessRank >= 8 && r.targetBusinessRank <= 19
          ).length || 0;

          const grayGridPoints = rankingData.results?.filter(r =>
            r.targetBusinessRank === null || r.targetBusinessRank === undefined || r.targetBusinessRank >= 20
          ).length || 0;

          logger.log('📊 [Performance Tracker] Grid Point Breakdown:', {
            greenGridPoints,
            yellowGridPoints,
            redGridPoints,
            grayGridPoints,
            totalGridPoints
          });

          // Compute a focused window around user's rank when rank > 10
          let aroundMe = [];
          if (myBusiness && myBusiness.rank > 10) {
            const endRank = myBusiness.rank - 1; // rank just above me
            const startRank = Math.max(1, endRank - 8); // 9 above me, or up to rank 1
            aroundMe = smartCompetitors
              .filter(c => c.rank >= startRank && c.rank <= endRank)
              .slice(0, 9); // ensure at most 9
          }

          const analysisData = {
            keyword: analysisKeyword,
            competitors: smartCompetitors.slice(0, 20),
            aroundMe, // top 9 ranks directly above my rank (if rank > 10)
            myBusinessRank: myBusiness?.rank || (smartCompetitors.findIndex(c => c.isMyBusiness) + 1) || null,
            myBusinessScore: typeof myBusiness?.gmbScore === 'number' ? myBusiness.gmbScore : null,
            myBusinessName: myBusiness?.name || businessName || businessData?.name || null,
            totalFound: smartCompetitors.length,
            searchLocation: formData.center,
            searchRadius: rankingData.searchRadius,
            gridSize: rankingData.gridSize,
            analysisType: 'Smart Radius Analysis',
            gridData: rankingData,
            timestamp: new Date().toISOString(),
            apiHealthStatus: apiHealthStatus,
            config: {
              radius: `${formData.radiusValue} ${formData.radiusUnit}`,
              grid: formData.mapGrid
            },
            storageKey,
            // Add grid point breakdown for Performance page Local Pack and Geo Rank Heatmap
            // Calculate directly from rankingData to ensure accuracy
            greenGridPoints: greenGridPoints,
            yellowGridPoints: yellowGridPoints,
            redGridPoints: redGridPoints,
            grayGridPoints: grayGridPoints,
            totalGridPoints: totalGridPoints || 0
          };
          newKeywordResults[analysisKeyword] = analysisData;
          lastAnalyzedKeywordData = analysisData;

          persistAnalysisData(analysisData);
          logger.log(`📁 Analysis completed for "${analysisKeyword}"`);
          logger.log('⚡ Optimized multi-keyword processing');

        } catch (err) {
          logger.error(`Error analyzing keyword "${analysisKeyword}":`, err);
        }
      }

      setKeywordResults(newKeywordResults);
      if (lastAnalyzedKeywordData) {
        setCompetitorData(lastAnalyzedKeywordData);
        setActiveKeywordTab(lastAnalyzedKeywordData.keyword);
      }

      clearAnalysisProgress();
      setProgress({ current: 100, total: 100, status: 'All keyword analyses complete!' });

    } catch (err) {
      logger.error('❌ Smart Radius Analysis failed:', err);
      setError('Analysis failed: ' + err.message);
    } finally {
      setAnalyzing(false);
      setAnalysisInProgress(false);
      setBackgroundAnalysisId(null);
    }
  }, [rankingService, formData, selectedKeywords, businessData, businessName, gridSizeNumber, radiusInKm, persistAnalysisProgress, clearAnalysisProgress, persistAnalysisData, backgroundAnalysisId, analysisInProgress, analysisLocked, daysUntilNextAnalysis, keywordResults]);

  // Calculate comprehensive GMB score using 23-factor scoring system (0-1000)
  const calculateGMBScore = (business, competitors = [], cityName, keyword) => {
    logger.log(`\n🔍 ===== GMB SCORING ANALYSIS FOR: ${business.name} =====`);
    // Ensure business has all required fields with safe defaults
    const safeBusiness = {
      // Basic fields
      name: business.title || business.name || 'Unknown Business',
      address: business.address || business.formattedAddress || 'Address not available',

      // Contact fields - DataForSEO uses 'phone' and 'url'
      phone: business.phone || business.internationalPhoneNumber || business.nationalPhoneNumber,
      internationalPhoneNumber: business.phone || business.internationalPhoneNumber,
      nationalPhoneNumber: business.nationalPhoneNumber,
      website: business.url || business.website || business.websiteUri,
      websiteUri: business.url || business.websiteUri || business.website,
      url: business.url || business.website, // DataForSEO field

      // Rating fields - DataForSEO uses 'rating.value' and 'rating.votes_count'
      rating: business.rating?.value || business.rating || 0,
      reviewCount: business.rating?.votes_count || business.reviewCount || business.userRatingCount || 0,
      userRatingCount: business.rating?.votes_count || business.userRatingCount || business.reviewCount || 0,

      // Photo fields - DataForSEO uses 'total_photos'
      photoCount: business.total_photos || business.photoCount || 0,
      total_photos: business.total_photos || business.photoCount || 0, // DataForSEO field

      // Category fields - DataForSEO uses 'category' and 'category_ids'
      categories: business.categories || business.types || [],
      primaryType: business.category || business.primaryType || business.types?.[0],
      category: business.category || business.primaryType, // DataForSEO field
      types: business.category_ids || business.types || business.categories || [],
      category_ids: business.category_ids || business.types, // DataForSEO field

      // Hours fields - DataForSEO uses 'work_hours'
      openingHours: business.openingHours || (business.work_hours?.timetable ? [business.work_hours.timetable] : []),
      work_hours: business.work_hours, // DataForSEO field

      // Additional fields
      socialLinks: business.socialLinks || [],
      appointmentLinks: business.appointmentLinks || [],
      book_online_url: business.book_online_url, // DataForSEO field
      serviceArea: business.serviceArea,
      hasQA: business.hasQA || false,
      reviewResponseRate: business.reviewResponseRate || 0,
      newReviewsLast30Days: business.newReviewsLast30Days || 0,
      is_claimed: business.is_claimed || false // DataForSEO field
    };

    logger.log(`📊 Raw Business Data:`, safeBusiness);

    // Use safeBusiness for all calculations
    business = safeBusiness;

    const factors = {
      namePts: calculateNameScore(business, cityName),
      addrPts: calculateAddressScore(business, cityName, keyword),
      phonePts: calculatePhoneScore(business, competitors),
      descPts: calculateDescriptionScore(business),
      websitePts: calculateWebsiteScore(business),
      hoursPts: calculateHoursScore(business),
      labelPts: calculateLabelsScore(business),
      catBasePts: calculateCategoryBaseScore(business),
      catExtraPts: calculateCategoryExtraScore(business),
      catCityPts: calculateCategoryCityScore(business, cityName),
      socialPts: calculateSocialScore(business),
      appointmentsPts: calculateAppointmentsScore(business),
      serviceAreaPts: calculateServiceAreaScore(business),
      bookApptPts: calculateBookAppointmentScore(business),
      qaPts: calculateQAScore(business),
      photosPts: calculatePhotosScore(business),
      reviewRatingPts: calculateReviewRatingScore(business),
      responseRatePts: calculateResponseRateScore(business),
      reviewsPts: calculateReviewsVsCompetitorsScore(business, competitors),
      velocityPts: calculateVelocityScore(business),
      feedPts: calculateFeedScore(business),
      productsPts: calculateProductsScore(business),
      servicesPts: calculateServicesScore(business)
    };

    logger.log(`📈 DETAILED SCORING BREAKDOWN:`);
    const name = business.title || business.name;
    const phone = business.phone || business.internationalPhoneNumber || business.nationalPhoneNumber;
    const website = business.url || business.websiteUri;
    const hasHours = business.work_hours?.timetable || (business.openingHours && business.openingHours.length > 0);
    const types = business.category_ids || business.types;
    const primaryType = business.category || business.primaryType;
    const photoCount = business.total_photos || business.photoCount || 0;
    const rating = business.rating?.value || business.rating || 0;
    const reviewCount = business.rating?.votes_count || business.userRatingCount || 0;

    logger.log(`   🏷️  Name Score: ${factors.namePts} pts (name: ${name}, contains city: ${cityName ? name?.toLowerCase().includes(cityName.toLowerCase()) : 'N/A'})`);
    logger.log(`   📍 Address Score: ${factors.addrPts} pts (has address: ${!!business.address})`);
    logger.log(`   📞 Phone Score: ${factors.phonePts} pts (has phone: ${!!phone})`);
    logger.log(`   📝 Description Score: ${factors.descPts} pts (default)`);
    logger.log(`   🌐 Website Score: ${factors.websitePts} pts (has website: ${!!website})`);
    logger.log(`   🕒 Hours Score: ${factors.hoursPts} pts (has hours: ${!!hasHours})`);
    logger.log(`   🏷️  Labels Score: ${factors.labelPts} pts (has types: ${!!(types?.length)})`);
    logger.log(`   📂 Category Base Score: ${factors.catBasePts} pts (primary type: ${primaryType || 'none'})`);
    logger.log(`   📂 Category Extra Score: ${factors.catExtraPts} pts (extra categories: ${Math.max(0, (business.categories?.length || 1) - 1)})`);
    logger.log(`   🏙️  Category City Score: ${factors.catCityPts} pts`);
    logger.log(`   📱 Social Score: ${factors.socialPts} pts (social links: ${business.socialLinks?.length || 0})`);
    logger.log(`   📅 Appointments Score: ${factors.appointmentsPts} pts (appointment links: ${business.appointmentLinks?.length || 0})`);
    logger.log(`   🗺️  Service Area Score: ${factors.serviceAreaPts} pts (has service area: ${!!business.serviceArea})`);
    logger.log(`   📖 Book Appointment Score: ${factors.bookApptPts} pts (book_online_url: ${!!business.book_online_url})`);
    logger.log(`   ❓ Q&A Score: ${factors.qaPts} pts (has Q&A: ${!!business.hasQA})`);
    logger.log(`   📸 Photos Score: ${factors.photosPts} pts (photo count: ${photoCount})`);
    logger.log(`   ⭐ Review Rating Score: ${factors.reviewRatingPts} pts (rating: ${rating})`);
    logger.log(`   💬 Response Rate Score: ${factors.responseRatePts} pts (response rate: ${business.reviewResponseRate || 0}%)`);
    logger.log(`   📊 Reviews vs Competitors Score: ${factors.reviewsPts} pts (reviews: ${reviewCount})`);
    logger.log(`   🚀 Velocity Score: ${factors.velocityPts} pts (new reviews: ${business.newReviewsLast30Days || 0})`);
    logger.log(`   📢 Feed Score: ${factors.feedPts} pts (default)`);
    logger.log(`   🛍️  Products Score: ${factors.productsPts} pts (default)`);
    logger.log(`   🔧 Services Score: ${factors.servicesPts} pts (default)`);

    const totalScore = Object.values(factors).reduce((sum, pts) => sum + pts, 0);
    const finalScore = Math.min(totalScore, 1000);

    logger.log(`🎯 TOTAL SCORE: ${finalScore}/1000 (raw: ${totalScore})`);
    logger.log(`🏆 ========================================\n`);

    return finalScore;
  };

  // Individual scoring functions
  const calculateNameScore = (business, cityName) => {
    // DataForSEO uses 'title' field
    const name = business.title || business.name;
    if (!name) return 0;
    if (cityName && name.toLowerCase().includes(cityName.toLowerCase())) {
      return 22;
    }
    return 10;
  };

  const calculateAddressScore = (business, cityName, keyword) => {
    if (!business.address) return 2;

    const addressLower = business.address.toLowerCase();
    const hasCity = cityName ? addressLower.includes(cityName.toLowerCase()) : false;
    const hasKeyword = keyword ? addressLower.includes(keyword.toLowerCase()) : false;

    if (hasCity && hasKeyword) return 24;
    return 12;
  };

  const calculatePhoneScore = (business, competitors) => {
    // DataForSEO uses 'phone' field
    const phone = business.phone || business.internationalPhoneNumber || business.nationalPhoneNumber;
    return phone ? 30 : 0;
  };

  const calculateDescriptionScore = (business) => {
    return 30; // Default 30 points for all businesses
  };

  const calculateWebsiteScore = (business) => {
    // DataForSEO uses 'url' field
    const website = business.url || business.websiteUri;
    return website ? 5 : 0;
  };

  const calculateHoursScore = (business) => {
    // DataForSEO uses 'work_hours.timetable' field
    const hasHours = business.work_hours?.timetable || (business.openingHours && business.openingHours.length > 0);
    return hasHours ? 14 : 0;
  };

  const calculateLabelsScore = (business) => {
    // DataForSEO uses 'category_ids' field
    const types = business.category_ids || business.types;
    return (types && types.length > 0) ? 10 : 0;
  };

  const calculateCategoryBaseScore = (business) => {
    // DataForSEO uses 'category' field
    const primaryType = business.category || business.primaryType;
    return primaryType ? 40 : 0;
  };

  const calculateCategoryExtraScore = (business) => {
    if (!business.categories || business.categories.length <= 1) return 0;
    const extraCategories = business.categories.length - 1;
    return Math.min(extraCategories * 5, 10);
  };

  const calculateCategoryCityScore = (business, cityName) => {
    if (!cityName || !business.categories) return 0;
    const cityLower = cityName.toLowerCase();
    const hasCity = business.categories.some(cat =>
      cat && typeof cat === 'string' && cat.toLowerCase().includes(cityLower)
    );
    return hasCity ? 20 : 0;
  };

  const calculateSocialScore = (business) => {
    if (!business.socialLinks || business.socialLinks.length === 0) return 0;
    const socialDomains = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'tiktok'];
    const hasSocial = business.socialLinks.some(link =>
      link && typeof link === 'string' && socialDomains.some(domain => link.toLowerCase().includes(domain))
    );
    return hasSocial ? 10 : 0;
  };

  const calculateAppointmentsScore = (business) => {
    return (business.appointmentLinks && business.appointmentLinks.length > 0) ? 5 : 0;
  };

  const calculateServiceAreaScore = (business) => {
    return business.serviceArea ? 10 : 0;
  };

  const calculateBookAppointmentScore = (business) => {
    // DataForSEO uses 'book_online_url' field
    if (business.book_online_url) return 5;

    const bookingTerms = ['book', 'reserve', 'appointment', 'schedule'];
    if (business.appointmentLinks) {
      const hasBookingTerms = business.appointmentLinks.some(link =>
        bookingTerms.some(term => link.toLowerCase().includes(term))
      );
      if (hasBookingTerms) return 5;
    }
    return 0;
  };

  const calculateQAScore = (business) => {
    return business.hasQA ? 5 : 0;
  };

  const calculatePhotosScore = (business) => {
    // DataForSEO uses 'total_photos' field
    const photoCount = business.total_photos || business.photoCount || 0;
    if (photoCount === 0) return 0;

    let score = 0;
    if (photoCount >= 1) score += 5;  // Cover photo equivalent
    if (photoCount >= 2) score += 10; // Logo equivalent
    if (photoCount >= 5) score += 40; // Volume ≥5

    return Math.min(score, 75);
  };

  const calculateReviewRatingScore = (business) => {
    // DataForSEO uses 'rating.value' field
    const rating = business.rating?.value || business.rating;
    if (!rating || rating <= 1) return 0;
    return Math.min((rating - 1) * 27.5, 110);
  };

  const calculateResponseRateScore = (business) => {
    if (!business.reviewResponseRate) return 0;
    return (business.reviewResponseRate / 100) * 8;
  };

  const calculateReviewsVsCompetitorsScore = (business, competitors) => {
    // DataForSEO uses 'rating.votes_count' field
    const reviewCount = business.rating?.votes_count || business.userRatingCount || 0;
    if (!reviewCount || competitors.length === 0) return 0;

    const competitorReviews = competitors
      .map(c => c.rating?.votes_count || c.userRatingCount || 0)
      .filter(count => count > 0)
      .sort((a, b) => b - a); // Sort descending (highest first)

    if (competitorReviews.length === 0) return 0;

    // Calculate top 5% and bottom 5% to exclude (outliers on both ends)
    const top5PercentCount = Math.max(1, Math.ceil(competitorReviews.length * 0.05));
    const bottom5PercentCount = Math.max(1, Math.ceil(competitorReviews.length * 0.05));

    // Remove top 5% and bottom 5% outliers
    let adjustedReviews = [...competitorReviews];
    const minRequiredLength = top5PercentCount + bottom5PercentCount + 1; // Need at least some middle values

    if (competitorReviews.length >= minRequiredLength) {
      // Remove the top 5% outliers (highest reviews)
      const topOutliers = adjustedReviews.splice(0, top5PercentCount);

      // Remove the bottom 5% outliers (lowest reviews)
      const bottomOutliers = adjustedReviews.splice(-bottom5PercentCount);

      logger.log(`📊 Review Score Calculation for ${business.name}:`);
      logger.log(`   Original reviews count: ${competitorReviews.length}`);
      logger.log(`   Original reviews: [${competitorReviews.slice(0, 10).join(', ')}...]`);
      logger.log(`   Removed top ${top5PercentCount} outliers (highest): [${topOutliers.join(', ')}]`);
      logger.log(`   Removed bottom ${bottom5PercentCount} outliers (lowest): [${bottomOutliers.join(', ')}]`);
      logger.log(`   Middle 90% reviews count: ${adjustedReviews.length}`);
      logger.log(`   Middle 90% reviews: [${adjustedReviews.slice(0, 10).join(', ')}...]`);
    } else {
      logger.log(`📊 Review Score Calculation for ${business.name}:`);
      logger.log(`   Not enough competitors (${competitorReviews.length}) to exclude top/bottom 5%`);
      logger.log(`   Using all reviews: [${competitorReviews.slice(0, 10).join(', ')}...]`);
    }

    const avgCompetitorReviews = adjustedReviews.reduce((sum, count) => sum + count, 0) / adjustedReviews.length;
    const score = Math.min((business.userRatingCount / avgCompetitorReviews) * 140, 140);

    logger.log(`   Business reviews: ${business.userRatingCount}`);
    logger.log(`   Adjusted avg competitor reviews: ${avgCompetitorReviews.toFixed(1)}`);
    logger.log(`   Final review score: ${score.toFixed(1)}/140`);

    return score;
  };

  const calculateVelocityScore = (business, benchmark = 20) => {
    if (!business.newReviewsLast30Days) return 0;
    return Math.min((business.newReviewsLast30Days / benchmark) * 12, 12);
  };

  const calculateFeedScore = (business) => {
    return 100; // Default 100 points for all businesses
  };

  const calculateProductsScore = (business) => {
    return 10; // Default 10 points for all businesses
  };

  const calculateServicesScore = (business) => {
    return 20;
  };

  // Calculate grid rank penalty based on average rank
  const calculateGridRankPenalty = (averageRank) => {
    if (averageRank <= 3) return 0;      // Ranks 1-3: No penalty
    if (averageRank <= 10) return 0.25;  // Ranks 4-10: 25% penalty
    if (averageRank <= 20) return 0.50;  // Ranks 11-20: 50% penalty
    return 0.75;                         // Ranks 20+: 75% penalty
  };

  // Calculate grid coverage penalty based on grid appearances
  const calculateGridCoveragePenalty = (gridAppearances, totalGridPoints) => {
    const coveragePercentage = (gridAppearances / totalGridPoints) * 100;

    if (coveragePercentage >= 80) return 0;      // 80%+ coverage: No penalty
    if (coveragePercentage >= 60) return 0.10;   // 60-79% coverage: 10% penalty
    if (coveragePercentage >= 40) return 0.20;   // 40-59% coverage: 20% penalty
    if (coveragePercentage >= 20) return 0.30;   // 20-39% coverage: 30% penalty
    return 0.40;                                 // <20% coverage: 40% penalty
  };

  // Calculate ranking score based on position
  const calculateRankingScore = (business, rank) => {
    if (rank <= 3) return 100;
    if (rank <= 5) return 80;
    if (rank <= 10) return 60;
    if (rank <= 15) return 40;
    return 20;
  };

  // Enhanced export function
  const handleExport = async () => {
    if (!competitorData) return;

    setExporting(true);
    try {
      const exportData = competitorData.gridData;
      const filename = `smart-radius-analysis-${competitorData.keyword}-${new Date().toISOString().split('T')[0]}`;

      if (exportFormat === 'json') {
        dataExportService.exportToJSON(exportData, `${filename}.json`);
      } else {
        dataExportService.exportToCSV(exportData, `${filename}.csv`);
      }

    } catch (err) {
      logger.error('❌ Export error:', err);
      setError('Failed to export data: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Render Ranking Podium
  const renderRankingPodium = () => {
    if (!competitorData?.competitors || competitorData.competitors.length < 3) return null;

    const topThree = competitorData.competitors.slice(0, 3);

    return (
      <RankingPodium>
        <PodiumContainer>
          {/* Position 2 - Blurred */}
          <PodiumStep position={2} sx={{ filter: 'blur(6px)', userSelect: 'none' }}>
            <CompetitorAvatar>
              <BusinessIcon sx={{ fontSize: '16px', color: '#666' }} />
            </CompetitorAvatar>
            <CompetitorName>{topThree[1]?.name || 'N/A'}</CompetitorName>
            <PodiumBase position={2}>2</PodiumBase>
          </PodiumStep>

          {/* Position 1 - Visible */}
          <PodiumStep position={1}>
            <CrownIcon>👑</CrownIcon>
            <CompetitorAvatar>
              <BusinessIcon sx={{ fontSize: '16px', color: '#666' }} />
            </CompetitorAvatar>
            <CompetitorName>{topThree[0]?.name || 'N/A'}</CompetitorName>
            <PodiumBase position={1}>1</PodiumBase>
          </PodiumStep>

          {/* Position 3 - Blurred */}
          <PodiumStep position={3} sx={{ filter: 'blur(6px)', userSelect: 'none' }}>
            <CompetitorAvatar>
              <BusinessIcon sx={{ fontSize: '16px', color: '#666' }} />
            </CompetitorAvatar>
            <CompetitorName>{topThree[2]?.name || 'N/A'}</CompetitorName>
            <PodiumBase position={3}>3</PodiumBase>
          </PodiumStep>
        </PodiumContainer>
      </RankingPodium>
    );
  };

  // Render Comparison Table
  const renderComparisonTable = () => {
    if (!competitorData) return null;

    // PERFORMANCE: Pre-calculate rank map to avoid findIndex() in render loop (for fallback cases)
    const rankMap = new Map();
    (competitorData.competitors || []).forEach((comp, index) => {
      const key = comp.placeId || `${comp.name}-${comp.address}`;
      rankMap.set(key, (comp.rank || index + 1));
    });

    // Determine user's business and rank from available data
    const foundMyInListIndex = (competitorData.competitors || []).findIndex(c => c.isMyBusiness);
    const myBusinessData =
      foundMyInListIndex !== -1
        ? competitorData.competitors[foundMyInListIndex]
        : competitorData.myBusiness || null;

    const myRank = myBusinessData?.rank || (foundMyInListIndex !== -1 ? foundMyInListIndex + 1 : null);
    const totalCompetitors = competitorData.totalFound || (competitorData.competitors ? competitorData.competitors.length : 0);

    // Enhanced logging for debugging
    logger.log('\n🔍 ===== BUSINESS RANKING DEBUG INFO =====');
    logger.log(`📍 Total competitors analyzed: ${totalCompetitors}`);
    logger.log(`🔎 Searching for business with isMyBusiness flag...`);
    logger.log(`📊 My Business Index: ${foundMyInListIndex} (${foundMyInListIndex === -1 ? 'NOT FOUND' : 'FOUND'})`);
    if (foundMyInListIndex !== -1) {
      const myBusiness = competitorData.competitors[foundMyInListIndex];
      logger.log(`✅ YOUR BUSINESS FOUND:`);
      logger.log(`   📛 Name: ${myBusiness.name}`);
      logger.log(`   🏆 Rank: ${myRank} out of ${totalCompetitors}`);
      logger.log(`   📍 Address: ${myBusiness.address || 'N/A'}`);
      logger.log(`   ⭐ Rating: ${myBusiness.rating || 'N/A'}`);
      logger.log(`   💬 Reviews: ${myBusiness.userRatingCount || 0}`);
      logger.log(`   📊 GMB Score: ${Math.round(myBusiness.gmbScore || 0)}/1000`);
    } else {
      logger.log(`❌ YOUR BUSINESS NOT FOUND in results`);
    }
    logger.log('🔍 ========================================\n');

    // Get competitor limit based on plan: basic=1, pro=3, advance=5
    const competitorLimit = getCompetitorGmbsLimit(effectivePlan);
    logger.log(`📊 Plan: ${effectivePlan}, Competitor Limit: ${competitorLimit}`);

    // Display ALL competitors, but track which ones should be blurred
    const allCompetitorsList = competitorData.competitors || [];
    let displayCompetitors = allCompetitorsList;

    // Determine which competitor indices should NOT be blurred (visible)
    // Always show: user's business + competitors directly above them (by rank) up to the limit
    const visibleIndices = new Set();

    if (myRank && foundMyInListIndex !== -1) {
      // Add user's business index as visible
      visibleIndices.add(foundMyInListIndex);

      // If user is at rank 1 (top), show competitors BELOW them up to the limit
      if (foundMyInListIndex === 0) {
        // User is #1, show competitors below them (rank 2, 3, 4, etc.)
        for (let i = 1; i <= Math.min(competitorLimit, allCompetitorsList.length - 1); i++) {
          visibleIndices.add(i);
        }
      } else {
        // User is not #1 - show competitors directly above the user (those with rank just below user's rank)
        // Start from the competitor immediately above user and go up
        const startIdx = Math.max(0, foundMyInListIndex - competitorLimit);
        for (let i = startIdx; i < foundMyInListIndex; i++) {
          visibleIndices.add(i);
        }
      }
    } else {
      // User not found - show top N competitors unblurred
      for (let i = 0; i < Math.min(competitorLimit, allCompetitorsList.length); i++) {
        visibleIndices.add(i);
      }
    }

    // Helper function to check if a competitor should be blurred
    const shouldBlurCompetitor = (index, competitor) => {
      if (competitor.isMyBusiness) return false; // Never blur user's business
      return !visibleIndices.has(index);
    };

    logger.log(`📊 Display Strategy: Showing all ${displayCompetitors.length} competitors, ${visibleIndices.size} visible, rest blurred`);

    return (
      <>
        {/* Ranking Status Banner */}
        {myRank ? (
          <Alert
            severity={myRank <= 3 ? 'success' : myRank <= 10 ? 'info' : 'warning'}
            icon={myRank <= 3 ? '🏆' : myRank <= 10 ? '📊' : '⚠️'}
            sx={{
              mb: 2,
              backgroundColor: myRank <= 3 ? '#d1fae5' : myRank <= 10 ? '#dbeafe' : '#fef3c7',
              border: `2px solid ${myRank <= 3 ? '#10b981' : myRank <= 10 ? '#3b82f6' : '#f59e0b'}`,
              '& .MuiAlert-icon': {
                fontSize: '24px'
              }
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {myRank <= 3 ? '🎉 Excellent! Your Business is in Top 3!' :
                  myRank <= 10 ? '✅ Your Business Found in Top 10' :
                    `📍 Your Business Found at Rank ${myRank}`}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '13px' }}>
                <strong>Your Rank:</strong> #{myRank} out of {totalCompetitors} competitors |
                <strong> GMB Score:</strong> {Math.round((myBusinessData?.gmbScore || 0))}/1000 |
                <strong> Rating:</strong> {myBusinessData?.rating ? Number(typeof myBusinessData.rating === 'object' ? myBusinessData.rating?.value : myBusinessData.rating).toFixed(1) : 'N/A'} ⭐ |
                <strong> Reviews:</strong> {myBusinessData?.userRatingCount || 0}
              </Typography>
              {myRank > 10 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: '#92400e' }}>
                  💡 Showing top 10 competitors above your ranking + your business position
                </Typography>
              )}
            </Box>
          </Alert>
        ) : (
          <Alert
            severity="error"
            icon="❌"
            sx={{
              mb: 2,
              backgroundColor: '#fee2e2',
              border: '2px solid #ef4444'
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your Business Not Found in Results
              </Typography>
            </Box>
          </Alert>
        )}

        <ComparisonTable component={Paper}>


          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Rank</TableHeaderCell>
                <TableHeaderCell>Business Name</TableHeaderCell>
                <TableHeaderCell>GMB Score</TableHeaderCell>
                <TableHeaderCell>Rating</TableHeaderCell>
                <TableHeaderCell>Reviews</TableHeaderCell>
                <TableHeaderCell>Tasks</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayCompetitors.map((competitor, displayIndex) => {
                // PERFORMANCE: Use pre-calculated rank map instead of findIndex()
                const competitorKey = competitor.placeId || `${competitor.name}-${competitor.address}`;
                const actualRank = rankMap.get(competitorKey) || displayIndex + 1;

                // Check if this competitor should be blurred
                const isBlurred = shouldBlurCompetitor(displayIndex, competitor);

                return (
                  <React.Fragment key={competitor.placeId || actualRank}>
                    <TableRow sx={{
                      position: 'relative',
                      ...(isBlurred && {
                        '& td': {
                          filter: 'blur(6px)',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }
                      })
                    }}>
                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <RankBadge>
                          {actualRank <= 3 ? (
                            <TrendingUpIcon width={16} height={16} color="#34A853" />
                          ) : (
                            <TrendingDownIcon width={16} height={16} color="#EF232A" />
                          )}
                          {actualRank}
                        </RankBadge>
                      </TableDataCell>

                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <BusinessNameCell>
                          <BusinessName
                            isMyBusiness={competitor.isMyBusiness}
                          >
                            {competitor.isMyBusiness ? `${competitor.name} (YOU)` : competitor.name}
                          </BusinessName>
                          <BusinessAddress>
                            <LocationOnIcon sx={{ fontSize: 10, color: '#6b7280', mr: 0.5 }} />
                            {competitor.address || 'No address'}
                          </BusinessAddress>
                          <BusinessIcons>
                            {competitor.websiteUri && (
                              <Tooltip title="Has Website">
                                <WebsiteIcon sx={{ fontSize: 12, color: '#10b981' }} />
                              </Tooltip>
                            )}
                            {(competitor.internationalPhoneNumber || competitor.nationalPhoneNumber) && (
                              <Tooltip title="Has Phone">
                                <PhoneIcon sx={{ fontSize: 12, color: '#10b981' }} />
                              </Tooltip>
                            )}
                            {competitor.photoCount > 0 && (
                              <Tooltip title={`${competitor.photoCount} Photos`}>
                                <PhotoIcon sx={{ fontSize: 12, color: '#10b981' }} />
                              </Tooltip>
                            )}
                          </BusinessIcons>
                        </BusinessNameCell>
                      </TableDataCell>

                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <Tooltip
                          title="GMB Score"
                          placement="top"
                          arrow
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', cursor: 'help' }}>
                            <ScoreText>
                              {Math.round(competitor.gmbScore || 0)}/1000
                            </ScoreText>
                            <LinearProgress
                              variant="determinate"
                              value={(competitor.gmbScore || 0) / 10}
                              sx={{
                                mt: 0.5,
                                height: 4,
                                width: '100%',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: (competitor.gmbScore || 0) >= 800 ? '#10b981' :
                                    (competitor.gmbScore || 0) >= 600 ? '#f59e0b' : '#ef4444'
                                }
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableDataCell>

                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating
                            value={typeof competitor.rating === 'object' ? Number(competitor.rating?.value || 0) : Number(competitor.rating || 0)}
                            readOnly
                            size="small"
                            precision={0.1}
                            sx={{ fontSize: '14px' }}
                          />
                          <ScoreText sx={{ fontSize: '12px' }}>
                            {competitor.rating ? Number(typeof competitor.rating === 'object' ? competitor.rating?.value : competitor.rating).toFixed(1) : 'N/A'}
                          </ScoreText>
                        </Box>
                      </TableDataCell>

                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <ScoreText>
                          {competitor.userRatingCount || 0}
                        </ScoreText>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '10px', color: '#666' }}>
                          reviews
                        </Typography>
                      </TableDataCell>

                      <TableDataCell isMyBusiness={competitor.isMyBusiness}>
                        <Chip
                          size="small"
                          label={competitor.isMyBusiness ? "View Tasks" : "N/A"}
                          sx={{
                            fontSize: '10px',
                            height: '24px',
                            backgroundColor: competitor.isMyBusiness ? '#0B91D6' : '#f3f4f6',
                            color: competitor.isMyBusiness ? 'white' : '#666',
                            cursor: competitor.isMyBusiness ? 'pointer' : 'default',
                            '&:hover': {
                              backgroundColor: competitor.isMyBusiness ? '#0980c2' : '#f3f4f6'
                            }
                          }}
                          onClick={competitor.isMyBusiness ? () => {
                            onClose();
                            navigate('/task-management');
                          } : undefined}
                        />
                      </TableDataCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </ComparisonTable>

        {/* Upgrade Banner - show when competitors are blurred */}
        {displayCompetitors.length > visibleIndices.size && (
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#FEF3C7',
            borderRadius: '8px',
            border: '1px solid #F59E0B',
            textAlign: 'center'
          }}>
            <Typography sx={{ fontWeight: 600, color: '#92400E', mb: 1 }}>
              🔒 Upgrade to see more competitors
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#92400E', mb: 1.5 }}>
              {effectivePlan === 'basic' || !effectivePlan
                ? 'Upgrade to Pro to see 3 competitors or Advanced to see 5 competitors'
                : effectivePlan === 'pro'
                  ? 'Upgrade to Advanced to see all 5 competitors'
                  : 'You can see up to 5 competitors with your plan'}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                onClose();
                navigate('/pricing');
              }}
              sx={{
                backgroundColor: '#F59E0B',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#D97706' }
              }}
            >
              View Plans
            </Button>
          </Box>
        )}
      </>
    );
  };

  if (!open) return null;

  const topThree = competitorData?.competitors?.slice(0, 3) || [];
  const allCompetitors = competitorData?.competitors || [];

  return (
    <Modal
      open={!!open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Fade in={!!open}>
        <ModalContent>
          <ModalHeader>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ModalTitle>{businessName}</ModalTitle>
              {analysisInProgress && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#0B91D6' }} />
                  <Typography variant="caption" sx={{ color: '#0B91D6', fontWeight: 600 }}>
                    Analysis in progress... ({Math.round(progress.current || 0)}%)
                  </Typography>
                </Box>
              )}
              {isDataFromCache && !analysisInProgress && (
                <Chip
                  size="small"
                  label="📊 Cached Results"
                  sx={{
                    backgroundColor: '#f0f9ff',
                    color: '#0B91D6',
                    fontWeight: 500,
                    fontSize: '11px',
                    maxWidth: 'fit-content'
                  }}
                  onClick={handleClearCache}
                  onDelete={handleClearCache}
                />
              )}
            </Box>
            <Box sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              '@media (max-width: 960px)': {
                width: '100%',
                justifyContent: 'flex-start'
              },
              '@media (max-width: 600px)': {
                gap: 1
              }
            }}>
              {competitorData && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleSmartRadiusAnalysis}
                    disabled={analyzing || analysisLocked}
                    title={analysisLocked ? analysisLockMessage : undefined}
                    startIcon={analyzing ? <CircularProgress size={16} /> : null}
                    sx={{
                      backgroundColor: '#0B91D6',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'none',
                      opacity: analysisLocked ? 0.6 : 1,
                      '&:hover': { backgroundColor: '#0980c2' },
                      '&:disabled': { backgroundColor: '#9ca3af' },
                      '@media (max-width: 600px)': {
                        fontSize: '13px',
                        padding: '8px 16px'
                      }
                    }}
                  >
                    {analyzing ? 'Reanalyzing...' : '🔄 Reanalyze'}
                  </Button>
                  {analysisLocked && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, width: '100%', display: 'block' }}
                    >
                      {analysisLockMessage}
                    </Typography>
                  )}
                  {isAdmin && (
                    <>
                      <Button
                        onClick={handleExport}
                        disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={16} /> : null}
                        sx={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          '&:hover': { backgroundColor: '#059669' },
                          '@media (max-width: 600px)': {
                            fontSize: '13px',
                            padding: '8px 16px'
                          }
                        }}
                      >
                        {exporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const newFormat = exportFormat === 'csv' ? 'json' : 'csv';
                          setExportFormat(newFormat);
                        }}
                        sx={{
                          fontSize: '10px',
                          minWidth: '60px',
                          height: '32px',
                          textTransform: 'none',
                          '@media (max-width: 600px)': {
                            fontSize: '9px',
                            minWidth: '50px',
                            height: '28px',
                            padding: '4px 8px'
                          }
                        }}
                      >
                        Switch to {exportFormat === 'csv' ? 'JSON' : 'CSV'}
                      </Button>
                    </>
                  )}
                </>
              )}
              <IconButton onClick={onClose} sx={{
                '@media (max-width: 960px)': {
                  marginLeft: 'auto'
                }
              }} data-tutorial="competitor-close">
                <CloseIcon />
              </IconButton>
            </Box>
          </ModalHeader>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Keyword Selection */}
          <Box sx={{ mb: 3 }} data-tutorial="competitor-keywords">
            <Typography variant="h6" sx={{ mb: 2, color: '#0B91D6', fontWeight: 600 }}>
              🔍 Select Analysis Keyword
            </Typography>
            <KeywordTabs>
              {keywordsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Loading business-specific keywords...
                  </Typography>
                </Box>
              ) : (
                dynamicKeywords.map((keyword) => (
                  <KeywordTab
                    key={keyword}
                    active={selectedKeywords.includes(keyword)}
                    onClick={() => toggleKeyword(keyword)}
                  >
                    {keyword}
                  </KeywordTab>
                ))
              )}
              <KeywordTab
                active={isCustomKeywordSelected}
                onClick={() => setIsCustomKeywordSelected(!isCustomKeywordSelected)}
              >
                Custom
              </KeywordTab>
            </KeywordTabs>
          </Box>

          {/* Custom Keywords Section */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#0B91D6', fontWeight: 600 }}>
                📝 My Custom Keywords ({customKeywords.length}/5)
              </Typography>
              {customKeywords.length < 5 && !showKeywordInput && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowKeywordInput(true)}
                  sx={{
                    borderColor: '#0B91D6',
                    color: '#0B91D6',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#0980c2',
                      backgroundColor: 'rgba(11, 145, 214, 0.04)'
                    }
                  }}
                >
                  + Add Keyword
                </Button>
              )}
            </Box>

            {/* Keyword Input Field */}
            {showKeywordInput && (
              <Box sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <Typography variant="body2" sx={{ mb: 1.5, color: '#6B7280', fontWeight: 500 }}>
                  Add a custom keyword to analyze your business ranking
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., Plumber, Lawyer, Gym, Restaurant..."
                    value={newKeywordInput}
                    onChange={(e) => setNewKeywordInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newKeywordInput.trim()) {
                        const trimmedKeyword = newKeywordInput.trim();
                        if (!customKeywords.includes(trimmedKeyword) && !dynamicKeywords.includes(trimmedKeyword)) {
                          setCustomKeywords(prev => [...prev, trimmedKeyword]);
                          toggleKeyword(trimmedKeyword);
                          setNewKeywordInput('');
                          setShowKeywordInput(false);
                        } else {
                          setError('This keyword already exists!');
                          setTimeout(() => setError(''), 3000);
                        }
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                          borderColor: '#0B91D6',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0B91D6',
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (newKeywordInput.trim()) {
                        const trimmedKeyword = newKeywordInput.trim();
                        if (!customKeywords.includes(trimmedKeyword) && !dynamicKeywords.includes(trimmedKeyword)) {
                          setCustomKeywords(prev => [...prev, trimmedKeyword]);
                          toggleKeyword(trimmedKeyword);
                          setNewKeywordInput('');
                          setShowKeywordInput(false);
                        } else {
                          setError('This keyword already exists!');
                          setTimeout(() => setError(''), 3000);
                        }
                      }
                    }}
                    sx={{
                      backgroundColor: '#0B91D6',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        backgroundColor: '#0980c2'
                      },
                      minWidth: '80px'
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowKeywordInput(false);
                      setNewKeywordInput('');
                    }}
                    sx={{
                      borderColor: '#E5E7EB',
                      color: '#6B7280',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#D1D5DB',
                        backgroundColor: '#F9FAFB'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9CA3AF' }}>
                  Press Enter to add or click the Add button
                </Typography>
              </Box>
            )}

            {customKeywords.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {customKeywords.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onClick={() => toggleKeyword(keyword)}
                    onDelete={() => {
                      setCustomKeywords(prev => prev.filter(k => k !== keyword));
                      setSelectedKeywords(prev => prev.filter(k => k !== keyword));
                      // Remove results for this keyword
                      setKeywordResults(prev => {
                        const newResults = { ...prev };
                        delete newResults[keyword];
                        return newResults;
                      });
                      // If this was the active keyword, switch to another
                      if (activeKeywordTab === keyword) {
                        const remainingKeywords = Object.keys(keywordResults).filter(k => k !== keyword);
                        setActiveKeywordTab(remainingKeywords[0] || null);
                      }
                    }}
                    sx={{
                      backgroundColor: selectedKeywords.includes(keyword) ? '#0B91D6' : '#f3f4f6',
                      color: selectedKeywords.includes(keyword) ? 'white' : '#374151',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedKeywords.includes(keyword) ? '#0980c2' : '#e5e7eb'
                      },
                      '& .MuiChip-deleteIcon': {
                        color: selectedKeywords.includes(keyword) ? 'rgba(255,255,255,0.7)' : '#666',
                        '&:hover': {
                          color: selectedKeywords.includes(keyword) ? 'white' : '#333'
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {customKeywords.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                <Typography variant="body2">
                  Add up to 5 custom keywords to analyze your business ranking for different search terms.
                  Each keyword will show separate ranking results.
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Analysis Section */}
          <Paper sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#f8fafc',
            textAlign: 'center',
            '@media (max-width: 600px)': {
              p: 2
            }
          }}>
            {/* Cooldown Alert - Show when analysis is locked */}
            {analysisLocked && daysUntilNextAnalysis > 0 && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: '12px',
                  '& .MuiAlert-icon': { alignItems: 'center' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ⏳ Cooldown Active
                  </Typography>
                  <Chip
                    label={`${daysUntilNextAnalysis} day${daysUntilNextAnalysis > 1 ? 's' : ''} remaining`}
                    size="small"
                    sx={{
                      backgroundColor: '#fbbf24',
                      color: '#92400e',
                      fontWeight: 600
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: 0.5 }}>
                    You can run competitor analysis again after the cooldown period ends.
                  </Typography>
                </Box>
              </Alert>
            )}

            {!analyzing && !competitorData && (
              <Box>
                <ActionButton
                  onClick={handleSmartRadiusAnalysis}
                  disabled={analyzing || !rankingService || analysisLocked}
                  sx={{
                    minWidth: '200px',
                    opacity: analysisLocked ? 0.6 : 1
                  }}
                  title={analysisLocked ? analysisLockMessage : undefined}
                  data-tutorial="competitor-start"
                >
                  {analysisLocked ? `🔒 Locked (${daysUntilNextAnalysis}d)` : '🎯 Start Analysis'}
                </ActionButton>
              </Box>
            )}

            {analyzing && (
              <LoadingContainer>
                <LoadingCircleContainer>
                  <AnimatedCircle delay={0} />
                  <AnimatedCircle delay={0.5} />
                  <AnimatedCircle delay={1} />
                  <MainProgressCircle>
                    <ProgressText>
                      {Math.round(((progress.current || 0) / (progress.total || 100)) * 100)}%
                    </ProgressText>
                    <ProgressLabel>Analyzing</ProgressLabel>
                  </MainProgressCircle>
                </LoadingCircleContainer>

                <StatusText key={progress.status}>
                  {progress.status || 'Analyzing competitors...'}
                </StatusText>



                <AnimatedIconsContainer>
                  <AnimatedIconBox delay={0}>
                    <SearchIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                  </AnimatedIconBox>
                  <AnimatedIconBox delay={0.3}>
                    <TravelExploreIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                  </AnimatedIconBox>
                  <AnimatedIconBox delay={0.6}>
                    <AnalyticsIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                  </AnimatedIconBox>
                </AnimatedIconsContainer>
              </LoadingContainer>
            )}
          </Paper>

          {/* Compare with Competitors Section */}
          {(competitorData || Object.keys(keywordResults).length > 0) && (
            <CompetitorSection>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <SectionTitle sx={{ mb: 0 }}>Compare with Competitors</SectionTitle>
              </Box>

              {/* Keyword Results Tabs */}
              {Object.keys(keywordResults).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: '#6B7280', fontWeight: 500 }}>
                    📊 View Results by Keyword:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.keys(keywordResults).map((keyword) => (
                      <Chip
                        key={keyword}
                        label={`${keyword} (${keywordResults[keyword].myBusinessRank ? `Rank #${keywordResults[keyword].myBusinessRank}` : 'Not Ranked'})`}
                        onClick={() => {
                          setActiveKeywordTab(keyword);
                          setCompetitorData(keywordResults[keyword]);
                        }}
                        sx={{
                          backgroundColor: activeKeywordTab === keyword ? '#0B91D6' : '#ffffff',
                          color: activeKeywordTab === keyword ? 'white' : '#374151',
                          fontWeight: 600,
                          fontSize: '13px',
                          border: activeKeywordTab === keyword ? 'none' : '1px solid #e5e7eb',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: activeKeywordTab === keyword ? '#0980c2' : '#f9fafb',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Ranking Podium */}
              {competitorData && renderRankingPodium()}

              {/* Comparison Table */}
              {competitorData && renderComparisonTable()}

              {/* Get Growth With Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#0B91D6',
                    color: 'white',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    border: '1px solid #0B91D6',
                    '&:hover': {
                      backgroundColor: '#0980c2'
                    },
                    '@media (max-width: 600px)': {
                      fontSize: '13px',
                      padding: '10px 20px',
                      width: '100%',
                      maxWidth: '300px'
                    }
                  }}
                  onClick={() => {
                    onClose();
                    navigate('/task-management');
                  }}
                >
                  Get Growth with
                </Button>
              </Box>
            </CompetitorSection>
          )}

          {/* Call to Action Section */}
          {competitorData && (
            <Paper sx={{
              p: 4,
              mb: 3,
              background: 'linear-gradient(135deg, #0B91D6 0%, #0a7db7 100%)',
              color: 'white',
              textAlign: 'center',
              '@media (max-width: 960px)': {
                p: 3
              },
              '@media (max-width: 600px)': {
                p: 2
              }
            }}>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                mb: 2,
                '@media (max-width: 600px)': {
                  fontSize: '1.25rem'
                }
              }}>
                🚀 Ready to Outperform Your Competitors?
              </Typography>
              <Typography variant="body1" sx={{
                mb: 3,
                opacity: 0.9,
                lineHeight: 1.6,
                '@media (max-width: 600px)': {
                  fontSize: '0.875rem',
                  mb: 2
                }
              }}>
                To improve your ranking and outperform your competitors, complete your optimization tasks.
                Our AI has analyzed your profile and generated personalized recommendations to boost your local search visibility.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    onClose();
                    navigate('/task-management');
                  }}
                  sx={{
                    backgroundColor: 'white',
                    color: '#0B91D6',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    },
                    transition: 'all 0.3s ease',
                    '@media (max-width: 600px)': {
                      width: '100%',
                      maxWidth: '300px',
                      fontSize: '0.875rem',
                      px: 3,
                      py: 1.25
                    }
                  }}
                >
                  📋 See Your Tasks
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    onClose();
                    navigate('/profile-strength');
                  }}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    '@media (max-width: 600px)': {
                      width: '100%',
                      maxWidth: '300px',
                      fontSize: '0.875rem',
                      px: 3,
                      py: 1.25
                    }
                  }}
                >
                  📊 Profile Analysis
                </Button>
              </Box>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    ✅ AI-Generated Tasks
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    🎯 Competitor-Based Insights
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    📈 Ranking Improvements
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Grid View */}
          {competitorData && showGridView && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#0B91D6', fontWeight: 600 }}>
                🗺️ Grid Ranking Visualization
              </Typography>
              <Box sx={{ height: '600px', width: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                <HeatmapPreviewMap
                  center={formData.center}
                  gridSize={competitorData.gridSize || competitorData.gridData?.gridSize || 7}
                  radiusKm={competitorData.searchRadius || competitorData.gridData?.searchRadius || 2}
                  height={600}
                  gridData={competitorData.gridData}
                  showRankings={true}
                />
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#10b981', borderRadius: '50%' }} />
                  <Typography variant="caption">Rank 1-3 (Green)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#f59e0b', borderRadius: '50%' }} />
                  <Typography variant="caption">Rank 4-10 (Yellow)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#ef4444', borderRadius: '50%' }} />
                  <Typography variant="caption">Rank 11+ (Red)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#9ca3af', borderRadius: '50%' }} />
                  <Typography variant="caption">Not Ranked (Gray)</Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </ModalContent>
      </Fade>
    </Modal>
  );
};

// Export both the modal and wrapper
export { CompetitorAnalysisModal };
export default CompetitorAnalysisModal;


